import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FiGrid, FiList, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import ProductCard from '../components/product/ProductCard';
import api from '../services/api';

const SearchPage = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({
    price: { min: 0, max: 100000 },
    colors: [],
    sizes: [],
    productTypes: [],
    onSale: false
  });
  const [expandedFilters, setExpandedFilters] = useState({
    price: true,
    color: true,
    size: true,
    productType: true
  });

  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    setSearchQuery(query);
    fetchSearchResults(query);
  }, [location.search]);

  const fetchSearchResults = async (query) => {
    setLoading(true);
    try {
      const res = await api.get('/products');
      const allProducts = Array.isArray(res) ? res : (res.products || []);

      let results = allProducts;
      if (query && query.trim() !== '') {
        const lowerQuery = query.toLowerCase();
        results = allProducts.filter(p =>
          (p.name && p.name.toLowerCase().includes(lowerQuery)) ||
          (p.category?.name && p.category.name.toLowerCase().includes(lowerQuery)) ||
          (typeof p.category === 'string' && p.category.toLowerCase().includes(lowerQuery)) ||
          (p.colors && p.colors.some(c => c.toLowerCase().includes(lowerQuery))) ||
          (p.description && p.description.toLowerCase().includes(lowerQuery))
        );
      }

      const colorsSet = new Set();
      const sizesSet = new Set();
      const typesSet = new Set();

      results.forEach(p => {
        if (p.colors) p.colors.forEach(c => colorsSet.add(c));
        if (p.sizes) {
          p.sizes.forEach(s => {
            const sizeName = typeof s === 'object' ? s.name : s;
            sizesSet.add(sizeName);
          });
        }
        if (p.category) {
          const typeName = p.category.name || p.category;
          typesSet.add(typeName);
        }
      });

      setAvailableColors(Array.from(colorsSet));
      setAvailableSizes(Array.from(sizesSet));
      setAvailableTypes(Array.from(typesSet));

      setProducts(results);
      setFilteredProducts(results);
    } catch (err) {
      console.error("Failed to fetch search results", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let results = [...products];

    if (filters.price.min > 0 || filters.price.max < 100000) {
      results = results.filter(p => p.price >= filters.price.min && p.price <= filters.price.max);
    }
    
    if (filters.colors.length > 0) {
      results = results.filter(p => p.colors?.some(c => filters.colors.includes(c)));
    }
    
    if (filters.sizes.length > 0) {
      results = results.filter(p => p.sizes?.some(s => {
        const sizeName = typeof s === 'object' ? s.name : s;
        return filters.sizes.includes(sizeName);
      }));
    }
    
    if (filters.productTypes.length > 0) {
      results = results.filter(p => {
        const typeName = p.category?.name || p.category;
        return filters.productTypes.includes(typeName);
      });
    }
    
    if (filters.onSale) {
      results = results.filter(p => p.discount > 0 || p.originalPrice > p.price);
    }
    
    switch (sortBy) {
      case 'price-low': 
        results.sort((a, b) => a.price - b.price); 
        break;
      case 'price-high': 
        results.sort((a, b) => b.price - a.price); 
        break;
      case 'newest': 
        results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); 
        break;
      default: 
        break;
    }
    
    setFilteredProducts(results);
  }, [products, filters, sortBy]);

  const toggleFilter = (section) => {
    setExpandedFilters(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleColorChange = (color) => {
    setFilters(prev => ({
      ...prev,
      colors: prev.colors.includes(color) ? prev.colors.filter(c => c !== color) : [...prev.colors, color]
    }));
  };

  const handleSizeChange = (size) => {
    setFilters(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size) ? prev.sizes.filter(s => s !== size) : [...prev.sizes, size]
    }));
  };

  const handleProductTypeChange = (type) => {
    setFilters(prev => ({
      ...prev,
      productTypes: prev.productTypes.includes(type) ? prev.productTypes.filter(t => t !== type) : [...prev.productTypes, type]
    }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      price: { ...prev.price, [name]: parseInt(value) || 0 }
    }));
  };

  const clearFilters = () => {
    setFilters({
      price: { min: 0, max: 100000 },
      colors: [],
      sizes: [],
      productTypes: [],
      onSale: false
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-light uppercase tracking-[0.2em] text-gray-900">SEARCH</h1>
          {searchQuery && <p className="text-sm text-gray-500 mt-4">Showing results for: <span className="font-semibold text-gray-900">"{searchQuery}"</span></p>}
          <p className="text-sm text-gray-500 mt-1">{filteredProducts.length} results found</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                
                <div className="border-b border-gray-200 py-4">
                  <button onClick={() => toggleFilter('price')} className="flex items-center justify-between w-full text-left">
                    <span className="text-sm font-medium uppercase tracking-wide text-gray-800">PRICE</span>
                    {expandedFilters.price ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                  </button>
                  {expandedFilters.price && (
                    <div className="mt-4 space-y-4">
                      <div className="flex items-center gap-3">
                        <input type="number" name="min" value={filters.price.min} onChange={handlePriceChange} placeholder="Min" className="w-full px-3 py-2 border border-gray-300 rounded text-sm outline-none focus:border-black" />
                        <span className="text-gray-500">—</span>
                        <input type="number" name="max" value={filters.price.max} onChange={handlePriceChange} placeholder="Max" className="w-full px-3 py-2 border border-gray-300 rounded text-sm outline-none focus:border-black" />
                      </div>
                      
                      <div className="relative h-10 px-1">
                        <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 bg-gray-300 rounded-full" />
                        <div 
                          className="absolute top-1/2 h-1 -translate-y-1/2 bg-black rounded-full pointer-events-none" 
                          style={{ 
                            left: `${(filters.price.min / 100000) * 100}%`, 
                            width: `${((filters.price.max - filters.price.min) / 100000) * 100}%` 
                          }} 
                        />
                        <input 
                          type="range" 
                          min={0} 
                          max={100000} 
                          step={500} 
                          value={filters.price.min} 
                          onChange={(e) => { 
                            const val = Number(e.target.value); 
                            if (val <= filters.price.max) setFilters(prev => ({ ...prev, price: { ...prev.price, min: val } })); 
                          }} 
                          className="absolute top-1/2 left-0 w-full -translate-y-1/2 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-black [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-black [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto" 
                        />
                        <input 
                          type="range" 
                          min={0} 
                          max={100000} 
                          step={500} 
                          value={filters.price.max} 
                          onChange={(e) => { 
                            const val = Number(e.target.value); 
                            if (val >= filters.price.min) setFilters(prev => ({ ...prev, price: { ...prev.price, max: val } })); 
                          }} 
                          className="absolute top-1/2 left-0 w-full -translate-y-1/2 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-black [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-black [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto" 
                        />
                      </div>
                    </div>
                  )}
                </div>

                {availableColors.length > 0 && (
                  <div className="border-b border-gray-200 py-4">
                    <button onClick={() => toggleFilter('color')} className="flex items-center justify-between w-full text-left">
                      <span className="text-sm font-medium uppercase tracking-wide text-gray-800">COLOR</span>
                      {expandedFilters.color ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </button>
                    {expandedFilters.color && (
                      <div className="mt-4 space-y-2">
                        {availableColors.map(color => (
                          <label key={color} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={filters.colors.includes(color)} onChange={() => handleColorChange(color)} className="w-4 h-4 rounded border-gray-300" />
                            <span className="text-sm text-gray-700">{color}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {availableSizes.length > 0 && (
                  <div className="border-b border-gray-200 py-4">
                    <button onClick={() => toggleFilter('size')} className="flex items-center justify-between w-full text-left">
                      <span className="text-sm font-medium uppercase tracking-wide text-gray-800">SIZE</span>
                      {expandedFilters.size ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </button>
                    {expandedFilters.size && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {availableSizes.map(size => (
                          <button key={size} onClick={() => handleSizeChange(size)} className={`px-3 py-1 text-sm border rounded ${filters.sizes.includes(size) ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300'}`}>{size}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {availableTypes.length > 0 && (
                  <div className="border-b border-gray-200 py-4">
                    <button onClick={() => toggleFilter('productType')} className="flex items-center justify-between w-full text-left">
                      <span className="text-sm font-medium uppercase tracking-wide text-gray-800">PRODUCT TYPE</span>
                      {expandedFilters.productType ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </button>
                    {expandedFilters.productType && (
                      <div className="mt-4 space-y-2">
                        {availableTypes.map(type => (
                          <label key={type} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={filters.productTypes.includes(type)} onChange={() => handleProductTypeChange(type)} className="w-4 h-4 rounded border-gray-300" />
                            <span className="text-sm text-gray-700">{type}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="py-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={filters.onSale} onChange={(e) => setFilters(prev => ({ ...prev, onSale: e.target.checked }))} className="w-4 h-4 rounded border-gray-300" />
                    <span className="text-sm font-medium uppercase tracking-wide text-gray-800">SALE</span>
                  </label>
                </div>

                {(filters.colors.length > 0 || filters.sizes.length > 0 || filters.productTypes.length > 0 || filters.onSale || filters.price.min > 0 || filters.price.max < 100000) && (
                  <button onClick={clearFilters} className="mt-4 text-sm text-gray-500 hover:text-black underline">Clear all filters</button>
                )}
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">{filteredProducts.length} results</span>
                  <div className="w-px h-5 bg-gray-300"></div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Sort by:</span>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-sm border border-gray-300 rounded px-3 py-1.5 outline-none">
                      <option value="relevance">Relevance</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="newest">Newest First</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setViewMode('grid')} className={`p-2 border rounded ${viewMode === 'grid' ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600'}`}><FiGrid size={16} /></button>
                  <button onClick={() => setViewMode('list')} className={`p-2 border rounded ${viewMode === 'list' ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600'}`}><FiList size={16} /></button>
                </div>
              </div>

              {filteredProducts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-lg mb-4">No products found matching your criteria.</p>
                  <button onClick={clearFilters} className="text-blue-600 hover:underline">Clear all filters</button>
                </div>
              ) : (
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-10' : 'grid-cols-1 gap-6'}`}>
                  {filteredProducts.map(product => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;